'use client';

import { useEffect, useRef } from 'react';
import styles from './StarfieldBackground.module.css';

interface Star {
  x: number;
  y: number;
  size: number;
  speed: number;
  opacity: number;
  twinkleSpeed: number;
  twinkleOffset: number;
}

interface StarfieldBackgroundProps {
  /** Number of stars (default: 100) */
  starCount?: number;
  /** Base speed multiplier (default: 1) */
  speed?: number;
  /** Whether stars should twinkle (default: true) */
  twinkle?: boolean;
  /** Direction: 'up' | 'down' | 'left' | 'right' | 'radial' (default: 'up') */
  direction?: 'up' | 'down' | 'left' | 'right' | 'radial';
  /** Apply center mask to fade stars in middle (default: true) */
  centerMask?: boolean;
  /** Additional CSS class */
  className?: string;
}

export function StarfieldBackground({
  starCount = 100,
  speed = 1,
  twinkle = true,
  direction = 'up',
  centerMask = true,
  className = '',
}: StarfieldBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const starsRef = useRef<Star[]>([]);
  const animationRef = useRef<number | null>(null);
  const mouseRef = useRef({ x: 0, y: 0, active: false });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Initialize stars
    const initStars = () => {
      const rect = canvas.getBoundingClientRect();
      starsRef.current = Array.from({ length: starCount }, () => ({
        x: Math.random() * rect.width,
        y: Math.random() * rect.height,
        size: Math.random() * 2 + 0.5,
        speed: (Math.random() * 0.5 + 0.2) * speed,
        opacity: Math.random() * 0.7 + 0.3,
        twinkleSpeed: Math.random() * 0.02 + 0.01,
        twinkleOffset: Math.random() * Math.PI * 2,
      }));
    };

    initStars();

    // Mouse interaction
    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
        active: true,
      };
    };

    const handleMouseLeave = () => {
      mouseRef.current.active = false;
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);

    // Animation loop
    let time = 0;
    const animate = () => {
      const rect = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, rect.height);

      time += 0.016; // ~60fps

      // Center mask calculation
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      // Elliptical mask - wider than tall to cover content area
      const maskRadiusX = rect.width * 0.35;
      const maskRadiusY = rect.height * 0.4;

      starsRef.current.forEach((star) => {
        // Calculate twinkle
        let currentOpacity = star.opacity;
        if (twinkle) {
          currentOpacity = star.opacity * (0.5 + 0.5 * Math.sin(time * star.twinkleSpeed * 60 + star.twinkleOffset));
        }

        // Apply center mask - fade stars near center
        if (centerMask) {
          const dx = (star.x - centerX) / maskRadiusX;
          const dy = (star.y - centerY) / maskRadiusY;
          const distFromCenter = Math.sqrt(dx * dx + dy * dy);
          // Stars fully visible outside ellipse, fade inside
          const maskFactor = Math.min(1, Math.max(0, (distFromCenter - 0.3) / 0.7));
          currentOpacity *= maskFactor;
        }

        // Skip nearly invisible stars for performance
        if (currentOpacity < 0.02) {
          // Still move the star even if not drawn
          switch (direction) {
            case 'up':
              star.y -= star.speed;
              if (star.y < -10) star.y = rect.height + 10;
              break;
            case 'down':
              star.y += star.speed;
              if (star.y > rect.height + 10) star.y = -10;
              break;
            case 'left':
              star.x -= star.speed;
              if (star.x < -10) star.x = rect.width + 10;
              break;
            case 'right':
              star.x += star.speed;
              if (star.x > rect.width + 10) star.x = -10;
              break;
          }
          return;
        }

        // Mouse interaction - stars move away from cursor
        let offsetX = 0;
        let offsetY = 0;
        if (mouseRef.current.active) {
          const mdx = star.x - mouseRef.current.x;
          const mdy = star.y - mouseRef.current.y;
          const dist = Math.sqrt(mdx * mdx + mdy * mdy);
          const maxDist = 150;
          if (dist < maxDist && dist > 0) {
            const force = (1 - dist / maxDist) * 20;
            offsetX = (mdx / dist) * force;
            offsetY = (mdy / dist) * force;
          }
        }

        // Draw star with glow
        const drawX = star.x + offsetX;
        const drawY = star.y + offsetY;

        // Outer glow
        const gradient = ctx.createRadialGradient(
          drawX, drawY, 0,
          drawX, drawY, star.size * 3
        );
        gradient.addColorStop(0, `rgba(0, 255, 167, ${currentOpacity * 0.8})`);
        gradient.addColorStop(0.4, `rgba(0, 255, 211, ${currentOpacity * 0.3})`);
        gradient.addColorStop(1, 'rgba(0, 255, 167, 0)');

        ctx.beginPath();
        ctx.arc(drawX, drawY, star.size * 3, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        // Core
        ctx.beginPath();
        ctx.arc(drawX, drawY, star.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${currentOpacity})`;
        ctx.fill();

        // Move star based on direction
        switch (direction) {
          case 'up':
            star.y -= star.speed;
            if (star.y < -10) star.y = rect.height + 10;
            break;
          case 'down':
            star.y += star.speed;
            if (star.y > rect.height + 10) star.y = -10;
            break;
          case 'left':
            star.x -= star.speed;
            if (star.x < -10) star.x = rect.width + 10;
            break;
          case 'right':
            star.x += star.speed;
            if (star.x > rect.width + 10) star.x = -10;
            break;
          case 'radial':
            const centerX = rect.width / 2;
            const centerY = rect.height / 2;
            const dx = star.x - centerX;
            const dy = star.y - centerY;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist > 0) {
              star.x += (dx / dist) * star.speed;
              star.y += (dy / dist) * star.speed;
            }
            // Reset if too far
            const maxDist = Math.max(rect.width, rect.height);
            if (dist > maxDist) {
              star.x = centerX + (Math.random() - 0.5) * 100;
              star.y = centerY + (Math.random() - 0.5) * 100;
            }
            break;
        }
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [starCount, speed, twinkle, direction, centerMask]);

  return (
    <canvas
      ref={canvasRef}
      className={`${styles.canvas} ${className}`}
      aria-hidden="true"
    />
  );
}
